import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@design-system';
import { selectSessionAdvisor } from '@/model';
import { useAdvisorLogoUpload } from '@client/hooks/useAdvisorLogoUpload';
import CompanyLogoContainer from '../CompanyLogo';

export function AdvisorLogoField() {
  const advisor = useSelector(selectSessionAdvisor);
  const { uploadLogo, removeLogo, isSaving, error } = useAdvisorLogoUpload();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const hasLogo = !!advisor.advisorLogoKey;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadLogo(file);
    }
    event.target.value = '';
  };

  return (
    <div>
      <label className="block text-sm font-medium leading-6 text-gray-900">
        Organization Logo (Whitelabel)
      </label>
      <div className="mt-2 flex items-center gap-4">
        {hasLogo ? (
          <div className="bg-background rounded-md border border-gray-200 px-3 py-2">
            {/* Renders through the same component as the client header so the
                preview matches exactly what clients see (background included) */}
            <CompanyLogoContainer
              organizationDisplayName={advisor.organizationDisplayName}
              advisorLogoKey={advisor.advisorLogoKey}
              brandingPreference="logo"
            />
          </div>
        ) : (
          <span className="text-sm text-gray-500">No logo</span>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.svg"
          onChange={handleFileChange}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isSaving}
          onClick={() => fileInputRef.current?.click()}
        >
          {isSaving ? 'Saving...' : hasLogo ? 'Replace' : 'Upload'}
        </Button>
        {hasLogo && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isSaving}
            onClick={() => removeLogo()}
          >
            Remove
          </Button>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        PNG or SVG with a transparent background. Shown to clients in place of your organization
        name.
      </p>
      {error && <p className="mt-1 text-sm text-red-600">{error.message}</p>}
    </div>
  );
}

interface WhitelabelBrandingPreferenceFieldProps {
  value: WhitelabelBrandingPreference | undefined;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export function WhitelabelBrandingPreferenceField({
  value,
  onChange,
}: WhitelabelBrandingPreferenceFieldProps) {
  return (
    <fieldset>
      <legend className="block text-sm font-medium leading-6 text-gray-900">
        Client Header Branding
      </legend>
      <div className="mt-2 flex items-center gap-6">
        {(
          [
            { optionValue: 'name', label: 'Organization name' },
            { optionValue: 'logo', label: 'Logo' },
          ] as const
        ).map(({ optionValue, label }) => (
          <label
            key={optionValue}
            className="flex cursor-pointer items-center gap-2 text-sm text-gray-900"
          >
            <input
              type="radio"
              name="whitelabelBrandingPreference"
              value={optionValue}
              checked={value === optionValue}
              onChange={onChange}
              className="text-primary focus:ring-primary h-4 w-4 border-gray-300"
            />
            {label}
          </label>
        ))}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        What clients see at the top of their planning experience.
      </p>
    </fieldset>
  );
}
